import { EventEmitter } from '@/model/EventEmitter'
import { Feature, FeatureCollection } from '@turf/turf'
import { GeoJsonProperties, Geometry } from 'geojson'
import { DateTime } from 'luxon'
import { IRainGaugeService } from './IRainGaugeService'
import { SelectionEventArgs } from '../station/shared/SelectionManager'
import { FeatureMapper } from '../station/shared/FeatureMapper'

import { RainGaugeFactory } from './RainGaugeFactory'
import { RainGaugeTimeSeriesFactory } from './RainGaugeTimeSeriesFactory'
import { RainGauge } from '@/model/RainGauge'
import { RainGaugeAPI } from '@/api/raingauges/RainGaugeAPI'
import { SettingAPI } from '@/api/setting/SettingAPI'
import { RainGaugeTimeSeries } from '@/model/RainGaugeTimeSeries'
import { ServiceLocator } from '@/services/ServiceLocator'
import { BaseStationService } from '../station/BaseStationService'
import { ISettingService } from '@/services/setting/ISettingService'
import { LegendFactory } from '@/services/setting/LegendFactory'
import { Legend } from '@/model/Legend'

export class RainGaugeService extends BaseStationService<RainGauge> implements IRainGaugeService {
  private api: RainGaugeAPI = new RainGaugeAPI()
  private settingAPI: SettingAPI = new SettingAPI()
  private readonly settingService!: ISettingService

  readonly rainGaugeSelectionChanged: EventEmitter<SelectionEventArgs<RainGauge>> = this.selectionChanged
  private legend!: Legend

  constructor(locator: ServiceLocator) {
    super(locator)
    this.settingService = locator.get<ISettingService>('setting')
  }

  async toggleRainGauge(rainGauge: RainGauge): Promise<void> {
    this.toggleEntity(rainGauge.id, rainGauge)
  }

  clearRainGauges(): void {
    this.clearAllSelections()
  }

  async getRainGaugesOfCity(city: string, timestamp: DateTime): Promise<RainGauge[]> {
    try {
      const rainGaugeDTOS = await this.api.getRainGaugesForCity(city, timestamp)
      return Promise.resolve(RainGaugeFactory.fromDTO(rainGaugeDTOS))
    } catch (e) {
      return Promise.resolve([])
    }
  }

  async getTimeSeriesForRainGauge(rainGauge: RainGauge, timestamp: DateTime): Promise<RainGaugeTimeSeries> {
    const dto = await this.api.getTimeseriesForRainGaugeByTimestamp(rainGauge.id, timestamp.toUTC())
    return Promise.resolve(RainGaugeTimeSeriesFactory.fromDTO(dto))
  }

  async mapToFeatureCollection(rainGauges: RainGauge[]): Promise<FeatureCollection<Geometry, GeoJsonProperties>> {
    const legend = await this.getLegend()
    return FeatureMapper.toFeatureCollection(rainGauges, (s) => this.mapToFeature(s, legend))
  }

  private mapToFeature(rainGauge: RainGauge, legend: Legend): Feature<Geometry, GeoJsonProperties> {
    return {
      type: 'Feature',
      id: rainGauge.id,
      geometry: {
        type: 'Point',
        coordinates: rainGauge.getLngLat(),
      },
      properties: {
        id: rainGauge.id,
        name_no: `${rainGauge.name}`,
        color: this.getColor(rainGauge, legend),
      },
    }
  }

  /**
   * Load rain gauge legend from settings.
   * Uses the unified stations.rainfall legend structure.
   */
  private async getLegend(): Promise<Legend> {
    if (this.legend == null) {
      const rawSettings = await this.settingAPI.getRawSettings()

      if (rawSettings.legends.stations?.rainfall) {
        this.legend = LegendFactory.legendFromRawThresholds('model.raingauges', rawSettings.legends.stations.rainfall)
      } else {
        // Default legend if stations.rainfall is not configured
        this.legend = LegendFactory.legendFromRawThresholds('model.raingauges', [
          { value: -1, color: '#808080', labelText: 'No data' },
          { value: 0, color: '#90be6d', labelText: '0 mm' },
        ])
      }
    }

    return this.legend
  }

  private getColor(rainGauge: RainGauge, legend: Legend): string {
    const thresholds = legend.thresholds

    // Safety check: if no thresholds defined, return gray
    if (!thresholds || thresholds.length === 0) {
      return '#808080'
    }

    let cumulativeRainfall = -1
    if (rainGauge.rainfall) {
      cumulativeRainfall = rainGauge.rainfall.cumulative_value
    }

    // 0 and -1 are special cases
    if (cumulativeRainfall == -1) {
      return thresholds[0]?.color || '#808080'
    }
    if (cumulativeRainfall == 0) {
      return thresholds[1]?.color || thresholds[0]?.color || '#90be6d'
    }

    for (let i = 2; i < thresholds.length; i++) {
      const threshold = thresholds[i]
      if (cumulativeRainfall < threshold.value) {
        return threshold.color
      }
    }

    return thresholds[0]?.color || '#808080'
  }
}
